import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { setAppLocale, SUPPORTED_LOCALES } from '../i18n';
import { updateUserLocale } from '../api/localizationApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';

const { semantic } = colorTokens;

/**
 * @param {'icon'|'select'} variant
 * @param {boolean} persistToApi - POST /api/auth/locale when user is authenticated
 * @param {string} [label] - Select label (select variant)
 * @param {string} [helperText] - Select helper (select variant)
 */
export function LanguageSwitcher({
  variant = 'icon',
  persistToApi = false,
  size = 'small',
  label,
  helperText,
}) {
  const { t, i18n } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState(null);
  const languageLabel = label || t('language');
  const labelId = 'h05h-language-switcher-label';

  const getLocaleLabel = (code) => t(`locale_${code}`);

  const changeLocale = async (locale) => {
    setAppLocale(locale);
    setAnchorEl(null);

    if (persistToApi) {
      try {
        await updateUserLocale(locale);
      } catch {
        // Locale still applied locally; API sync is best-effort.
      }
    }
  };

  if (variant === 'select') {
    return (
      <FormControl size={size} sx={{ minWidth: 200 }} data-testid="language-switcher">
        <InputLabel
          id={labelId}
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 13,
            color: semantic.text.secondary,
          }}
        >
          {languageLabel}
        </InputLabel>
        <Select
          labelId={labelId}
          id="h05h-language-switcher"
          label={languageLabel}
          value={i18n.language}
          onChange={(event) => changeLocale(event.target.value)}
          inputProps={{
            'aria-labelledby': labelId,
            'data-testid': 'language-switcher-select',
          }}
          sx={{
            minHeight: 40,
            borderRadius: `${radiusTokens.search}px`,
            bgcolor: semantic.bg.input,
            color: semantic.text.primary,
            fontFamily: typographyTokens.fontFamily,
            fontSize: 13,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: semantic.border.default,
            },
          }}
        >
          {SUPPORTED_LOCALES.map((code) => (
            <MenuItem key={code} value={code}>
              {getLocaleLabel(code)}
            </MenuItem>
          ))}
        </Select>
        {helperText ? (
          <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: semantic.text.muted }}>
            {helperText}
          </span>
        ) : null}
      </FormControl>
    );
  }

  return (
    <>
      <Tooltip title={getLocaleLabel(i18n.language) || i18n.language}>
        <IconButton
          color="inherit"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size={size}
          aria-label={languageLabel}
          data-testid="language-switcher-icon"
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-label': languageLabel }}
      >
        {SUPPORTED_LOCALES.map((code) => (
          <MenuItem
            key={code}
            selected={code === i18n.language}
            onClick={() => changeLocale(code)}
          >
            {getLocaleLabel(code)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
